import { create } from 'zustand';
import { createRealtimeSync, type RealtimeSync } from '../../lib/realtime/createRealtimeSync';
import type { CRMState } from './types';
import { createAccountsSlice } from './accountsSlice';
import { createContactsSlice } from './contactsSlice';
import { createPipelineSlice } from './pipelineSlice';
import { createStripeSlice } from './stripeSlice';
import { createSupportSlice } from './supportSlice';

/**
 * Single Realtime subscription for the whole CRM domain. Any change to an
 * account, contact, opportunity, lead, pipeline stage or Stripe/lead product —
 * by a teammate, an Otto agent or a Stripe webhook — re-runs the orchestrated
 * load silently so the pipeline board and revenue dashboard stay current.
 */
let crmSync: RealtimeSync | null = null;

/** Lazily open the CRM Realtime subscription (idempotent). */
function ensureCRMRealtime(get: () => CRMState): void {
  if (!crmSync) {
    crmSync = createRealtimeSync({
      channelName: 'rt-crm',
      tables: [
        'crm_accounts', 'crm_contacts', 'crm_opportunities', 'crm_pipeline_stages',
        'onboarding_leads', 'stripe_products', 'stripe_subscriptions', 'lead_products',
      ],
      onChange: () => get().initializeCRM({ silent: true }),
    });
  }
  crmSync.subscribe();
}

export const useCRMStore = create<CRMState>((set, get) => ({
  // Compose all slices
  ...createAccountsSlice(set, get),
  ...createContactsSlice(set, get),
  ...createPipelineSlice(set, get),
  ...createStripeSlice(set, get),
  ...createSupportSlice(set, get),

  // Shared state (not owned by any single slice)
  loading: false,
  error: null,

  // Orchestration: loads all data in correct order. Each step delegates to the
  // owning slice's fetch, which normalizes the raw DB rows into the domain types
  // (role/status derivation, Json→Record, enum narrowing) — the single source of
  // truth for that shape. The orchestrator only sequences the dependencies.
  initializeCRM: async (opts) => {
    const silent = opts?.silent ?? false;
    if (!silent) set({ loading: true, error: null });
    try {
      // 1. Stages (nécessaires pour buildKanbanCards).
      await get().fetchStages();

      // 2-3. Stripe products and subscriptions, for the pipeline cards (buildKanbanCards).
      // The MRR does not depend on them: calculateRevenue reads get_mrr_breakdown.
      await get().fetchStripeProducts();
      await get().fetchStripeSubscriptions();

      // 4. Produits associés aux leads.
      await get().fetchLeadProducts();

      // 5. Leads, opportunités et comptes en parallèle.
      await Promise.all([
        get().fetchOnboardingLeads(),
        get().fetchOpportunities(),
        get().fetchAccounts(),
      ]);

      // 6. Tout est chargé : construire les cartes et calculer le revenu.
      get().buildKanbanCards();
      get().calculateRevenue();

      // Garder le pipeline et le revenu à jour en temps réel (idempotent).
      ensureCRMRealtime(get);

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to initialize CRM';
      set({ error: message });
      console.error('CRM initialization error:', error);
    } finally {
      set({ loading: false });
    }
  },
}));

// Re-export types and helpers for convenience
export type { CRMState, StripeSubscriptionMap, LeadProductsMap } from './types';
export { mapLeadStatusToStageIndex, mapStageIndexToLeadStatus, mapAccountStatusToStageIndex } from './helpers';
